import React, { useState, useEffect } from 'react';
import { CaseFile, ProceduralPhase } from '../types';

interface CaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (caseData: Partial<CaseFile>) => void;
  initialData?: CaseFile | null;
}

const PROCEDURAL_PHASES: ProceduralPhase[] = [
  'Urmărire penală / Fază prealabilă',
  'Cameră preliminară',
  'Fond',
  'Apel',
  'Recurs',
  'Contestaţie',
  'Revizuire',
  'Contestaţie în anulare',
  'Executare Silită',
  'Finalizat / Arhivat'
];

const CaseModal: React.FC<CaseModalProps> = ({ isOpen, onClose, onSave, initialData }) => {
  const [form, setForm] = useState<Partial<CaseFile>>({
    caseNumber: '',
    clientName: '',
    court: '',
    status: 'În lucru',
    phase: 'Fond',
    difficulty: 'medie',
    estimatedHours: 0,
    actualHours: 0
  });

  useEffect(() => {
    if (initialData) {
      setForm(initialData);
    } else {
      setForm({
        caseNumber: '',
        clientName: '',
        court: '',
        status: 'În lucru',
        phase: 'Fond',
        difficulty: 'medie',
        estimatedHours: 0,
        actualHours: 0
      });
    }
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(form);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-xl z-[200] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-[2rem] p-6 md:p-8 shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="flex justify-between items-center mb-6">
           <h4 className="text-xl md:text-2xl font-black italic text-slate-900">{initialData ? 'Editează Dosar' : 'Adaugă Dosar Nou'}</h4>
           <button onClick={onClose} className="w-8 h-8 flex items-center justify-center bg-slate-50 rounded-full text-slate-400 font-bold">✕</button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Număr Dosar</label>
            <input
              required
              className="w-full p-4 bg-slate-50 text-slate-900 rounded-xl outline-none font-bold text-sm border-2 border-transparent focus:border-indigo-500 transition-all"
              placeholder="Ex: 123/456/2024"
              value={form.caseNumber}
              onChange={e => setForm({...form, caseNumber: e.target.value})}
            />
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Nume Client</label>
            <input
              required
              className="w-full p-4 bg-slate-50 text-slate-900 rounded-xl outline-none font-bold text-sm border-2 border-transparent focus:border-indigo-500 transition-all"
              placeholder="Numele clientului"
              value={form.clientName}
              onChange={e => setForm({...form, clientName: e.target.value})}
            />
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Instanță</label>
            <input
              required
              className="w-full p-4 bg-slate-50 text-slate-900 rounded-xl outline-none font-bold text-sm border-2 border-transparent focus:border-indigo-500 transition-all"
              placeholder="Ex: Judecătoria Sector 1"
              value={form.court}
              onChange={e => setForm({...form, court: e.target.value})}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Status</label>
              <select
                aria-label="Status dosar"
                className="w-full p-4 bg-slate-50 text-slate-900 rounded-xl text-[10px] font-black uppercase border-2 border-transparent focus:border-indigo-500 transition-all"
                value={form.status}
                onChange={e => setForm({...form, status: e.target.value as CaseFile['status']})}
              >
                <option value="În lucru">În lucru</option>
                <option value="Închis - Câștigat">Închis - Câștigat</option>
                <option value="Închis - Pierdut">Închis - Pierdut</option>
                <option value="Suspendat">Suspendat</option>
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Fază Procesuală</label>
              <select
                aria-label="Fază procesuală"
                className="w-full p-4 bg-slate-50 text-slate-900 rounded-xl text-[10px] font-black uppercase border-2 border-transparent focus:border-indigo-500 transition-all"
                value={form.phase || 'Fond'}
                onChange={e => setForm({...form, phase: e.target.value as ProceduralPhase})}
              >
                {PROCEDURAL_PHASES.map(phase => (
                  <option key={phase} value={phase}>{phase}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <button type="button" onClick={onClose} className="flex-1 py-4 text-slate-400 font-black text-xs uppercase tracking-widest hover:bg-slate-50 rounded-xl transition-all">Anulează</button>
            <button type="submit" className="flex-1 py-4 bg-indigo-600 text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-xl shadow-indigo-200 hover:bg-indigo-700 active:scale-95 transition-all">
              {initialData ? 'Salvează Modificări' : 'Adaugă Dosar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CaseModal;
